import { useEffect, useState } from "react";
// next
import { useRouter } from 'next/router';
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
} from "@mui/material";;
// hooks
import useAuth from "../../hooks/useAuth";
import useResponsive from "../../hooks/useResponsive";
// components
import Page from "../../components/Page";
import Image from "../../components/Image";
import SvgIconStyle from "../../components/SvgIconStyle";
import Iconify from "../../components/Iconify";
import {
  ScoutRequestDialog,
  ScoutPoolTable,
  ScoutPoolList,
} from ".";
import {
  FliersDetailDrawer,
  FliersDetailDialog,
} from '../fliers';
// swr
import useSWR from 'swr';
// utils
import axios from '../../utils/axios';
import axiosSWRFetcher from '../../utils/swrFetcher';
// config
import { NAVBAR } from '../../config';
import { customShadows } from "../../theme/shadows";
// routes
import { PATH_SCOUT } from '../../routes/paths';

// ----------------------------------------------------------------------

export default function ProPage() {
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const lgUp = useResponsive('up', 'lg');
  const companyId = user?.employee?.company?.[0].id;
  const [openScoutRequest, setOpenScoutRequest] = useState(false);
  const router = useRouter();
  const { push, pathname, query } = router;

  const [isOpenFliersDetailDrawer, setIsOpenFliersDetailDrawer] = useState(false);
  const [isOpenFliersDetailDialog, setIsOpenFliersDetailDialog] = useState(false);
  const [selectedCoffeeChat, setSelectedCoffeeChat] = useState(null);
  const [currentTab, setCurrentTab] = useState("all");
  const [archivedCrew, setArchivedCrew] = useState({});
  const [page, setPage] = useState(1);
  const [pageQuery, setPageQuery] = useState("");

  const isArchived = currentTab === "archived";

  const { data: scouts, isLoading, mutate: mutateScout } = useSWR(companyId ? `/api/company/${companyId}/scout/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const { data: fliersCoffeeChats, isValidating: isLoadingCoffeeChat, mutate: mutateCoffeeChat } = useSWR((!isArchived && companyId) ? `/api/company/${companyId}/coffee-chat/?${pageQuery}status=${currentTab}` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const { data: coffeeChatSummary, mutate: mutateSummary } = useSWR(companyId ? `/api/company/${companyId}/coffee-chat/summary/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});

  const hasRecommendCrew = scouts?.length > 0;

  const selectCrew = (row) => {
    if (row) {
      setSelectedCoffeeChat(row);
      if (smUp) {
        setIsOpenFliersDetailDrawer(true);
      } else {
        setIsOpenFliersDetailDialog(true);
      }
    }
  };

  const openScoutRequestDialog = () => {
    setOpenScoutRequest(true);
  };

  const closeScoutRequestDialog = () => {
    setOpenScoutRequest(false);
    const newQuery = {
      ...query,
    };
    delete newQuery.feedback;
    push({ pathname, query: newQuery }, undefined, { shallow: true });
  };

  useEffect(() => {
    setPageQuery(`page=${page}&`);
  }, [page]);

  useEffect(() => {
    if (query?.profile && !isLoading) {
      fliersCoffeeChats?.results?.find((chat) => {
        if (parseInt(query?.profile) === chat?.crew?.id) {
          selectCrew(chat);
        }
      });
    }
    if (query?.feedback === "Y" || query?.feedback === "y") {
      openScoutRequestDialog();
    }
  }, [query, isLoading]);

  const loadArchivedCrew = async () => {
    try {
      const response = await axios.get(`/api/company/${companyId}/scout/archived/?${pageQuery}`);
      setArchivedCrew(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (isArchived) {
      loadArchivedCrew();
    }
  }, [pageQuery]);

  useEffect(() => {
    if (isArchived) {
      loadArchivedCrew();
    }
    setSelectedCoffeeChat(null);
    setPage(1);
  }, [currentTab]);

  useEffect(() => {
    if (!isLoadingCoffeeChat && selectedCoffeeChat?.crew?.id) {
      const found = fliersCoffeeChats?.results?.find((chat) => chat?.crew?.id === selectedCoffeeChat?.crew?.id);
      if (found) {
        setSelectedCoffeeChat(found);
      } else {
        setIsOpenFliersDetailDialog(false);
        setIsOpenFliersDetailDrawer(false);
      }
    }
  }, [isLoadingCoffeeChat]);

  return (
    <Page title="Scout">
      <Box sx={{ px: { xs: 3, sm: 5 }, pb: { xs: 10, sm : 12 }, mx: "auto" }} maxWidth="lg">
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 1 }}>
            <Image src={"/icons/scout-logo.svg"} sx={{ width: smUp ? "94px" : "82.25px", height: smUp ? "16px" : "14px", color: "primary.main" }} />
            <Typography variant="caption" sx={{ lineHeight: "12.8px" }}>
              커피챗을 제안해보세요!
            </Typography>
          </Stack>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<SvgIconStyle src={`/icons/icon-pencil-mono.svg`} sx={{ width: 16, height: 16, color: "text.secondary" }} />}
            onClick={() => push({pathname: PATH_SCOUT.about})}
          >
            회사 소개
          </Button>
        </Stack>

        {/* 추천크루 배너 */}
        {hasRecommendCrew && (
          <Stack
            direction="row"
            sx={{
              mb: 3,
              py: smUp ? 3 : 2,
              px: smUp ? 4 : 2.5,
              borderRadius: 1.5,
              backgroundColor: "common.black",
              boxShadow: customShadows.dark.z24,
              "&:hover": {
                borderColor: "primary.main",
                cursor: "pointer",
              },
              "&:active": {
                backgroundColor: "grey.800",
              },
            }}
            justifyContent="space-between"
            alignItems="center"
            onClick={() => push({pathname: PATH_SCOUT.recommend})}
          >
            <Stack
              direction="row"
              spacing={smUp ? 5 : 2}
              divider={!smUp && <Divider orientation="vertical" sx={{ color: "common.white" }} flexItem />}
              alignItems="center"
            >
              <Stack>
                <Typography variant="overline" sx={{ color: "common.white", textAlign: "center" }}>
                  새로운 추천
                </Typography>
                <Typography sx={{ color: "common.white", fontSize: smUp ? "40px" : "32px", lineHeight: smUp ? "40px" : "34px", textAlign: "center" }}>
                  {scouts?.length}
                </Typography>
              </Stack>

              <Stack>
                <Typography variant="h5" sx={{ color: "common.white" }}>
                  마음에 드는 크루를{!smUp && <br/>} 인재풀에 추가하세요!
                </Typography>
                {smUp && (
                <Typography variant="body2" sx={{ color: "common.white" }}>
                  인재풀에 추가하면, 커피챗을 제안할 수 있어요.
                </Typography>
                )}
              </Stack>

            </Stack>

            {smUp ? (
              <Button
                sx={{ color: "common.white" }}
                endIcon={<Iconify icon="ic:chevron-right" sx={{ width: "22px", height: "22px", color: "common.white" }} />}
              >
                추천 크루 확인하기
              </Button>
            ) : (
              <Iconify icon="ic:chevron-right" sx={{ mr: "-8px", width: "24px", height: "24px", color: "text.secondary" }} />
            )}
          </Stack>
        )}

        {/* 인재풀 목록 */}
        {smUp && (
          <ScoutPoolTable
            summaryData={coffeeChatSummary}
            scoutData={scouts}
            poolData={isArchived ? archivedCrew?.results : fliersCoffeeChats?.results}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            selectCrew={selectCrew}
            pageCount={isArchived ? archivedCrew?.page_count : fliersCoffeeChats?.page_count}
            page={page}
            setPage={setPage}
          />
        )}
        {!smUp && (
          <ScoutPoolList
            summaryData={coffeeChatSummary}
            scoutData={scouts}
            poolData={isArchived ? archivedCrew?.results : fliersCoffeeChats?.results}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            selectCrew={selectCrew}
            pageCount={isArchived ? archivedCrew?.page_count : fliersCoffeeChats?.page_count}
            page={page}
            setPage={setPage}
          />
        )}
      </Box>
      {/* 피드백 하단바 */}
      <Box
        sx={{
          px: 4,
          py: 2,
          backgroundColor: hasRecommendCrew ? "grey.200" : "common.black",
          position: "fixed",
          bottom: 0,
          width: lgUp ? `calc(100% - ${NAVBAR.DASHBOARD_WIDTH}px)` : 1,
          overflow: "clip",
          ":hover": {
            cursor: "pointer",
            backgroundColor: hasRecommendCrew ? "grey.300" : "grey.800",
          },
          ":active": {
            backgroundColor: hasRecommendCrew ? "grey.400" : "grey.700",
          },
        }}
        onClick={() => openScoutRequestDialog()}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems={"center"}
        >
          <SvgIconStyle src={`/icons/icon-user-plus-mono.svg`} sx={{ width: 20, height: 20, color: "text.disabled" }} />
          <Typography variant="subtitle2"
            sx={{ color: hasRecommendCrew ? "text.primary" : "common.white" }}
          >
            다음에는 어떤 조건으로 찾아드릴까요?
          </Typography>
          <Iconify icon="ic:chevron-right" sx={{ width: 20, height: 20, color: hasRecommendCrew ? "text.primary" : "common.white" }} />
        </Stack>
      </Box>
      <ScoutRequestDialog open={openScoutRequest} closeDialog={closeScoutRequestDialog} />
      <FliersDetailDrawer
        isOpen={isOpenFliersDetailDrawer}
        closeDrawer={() => setIsOpenFliersDetailDrawer(false)}
        coffeeChat={selectedCoffeeChat}
        reload={() => {mutateCoffeeChat(); mutateSummary();}}
        reloadScout={mutateScout}
      />
      <FliersDetailDialog
        isOpen={isOpenFliersDetailDialog}
        closeDialog={() => { setIsOpenFliersDetailDialog(false) }}
        coffeeChat={selectedCoffeeChat}
        reload={() => {mutateCoffeeChat(); mutateSummary();}}
        reloadScout={mutateScout}
      />
    </Page>
  )
}
